import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  X,
  Edit,
  Trash2,
  Printer,
  User,
  Stethoscope,
  FileText,
  Clipboard,
} from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { useDoctors } from "@/hooks/useDoctors";
import { useAuth } from "@/hooks/useAuth";

export default function NoteDetail({ note, onClose, onEdit, onDelete }) {
  const { user } = useAuth();
  const { data: doctors = [] } = useDoctors(user?.practiceId);
  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
  });
  if (!note) return null;

  const patient = patients.find((p) => p.id === note.patientId);
  const doctor = doctors.find((d) => d.id === note.doctorId);

  const handlePrint = () => {
    window.print();
  };

  const Section = ({ title, icon: Icon, children }) => (
    <div className="mb-8">
      <h3 className="flex items-center gap-3 text-xl font-semibold text-slate-900 border-b border-slate-200 pb-3 mb-4">
        <Icon className="w-6 h-6 text-blue-600" />
        {title}
      </h3>
      {children}
    </div>
  );

  // Function to render HTML content safely
  const renderHtmlContent = (htmlContent) => {
    if (!htmlContent) return null;

    return (
      <div
        className="quill-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        style={{
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: "1.6",
        }}
      />
    );
  };

  return (
    <>
      <style>
        {`
          .quill-content {
            font-family: inherit;
            font-size: inherit;
            line-height: 1.6;
          }
          .quill-content p {
            margin-bottom: 1em;
          }
          .quill-content p:last-child {
            margin-bottom: 0;
          }
          .quill-content strong {
            font-weight: 700;
          }
          .quill-content em {
            font-style: italic;
          }
          .quill-content u {
            text-decoration: underline;
          }
          .quill-content s {
            text-decoration: line-through;
          }
          .quill-content ul {
            list-style-type: disc;
            padding-left: 1.5em;
            margin-bottom: 1em;
          }
          .quill-content ol {
            list-style-type: decimal;
            padding-left: 1.5em;
            margin-bottom: 1em;
          }
          .quill-content li {
            margin-bottom: 0.25em;
          }
          .quill-content h1 {
            font-size: 1.5em;
            font-weight: 700;
            margin-bottom: 0.5em;
          }
          .quill-content h2 {
            font-size: 1.25em;
            font-weight: 700;
            margin-bottom: 0.5em;
          }
          .quill-content a {
            color: #3b82f6;
            text-decoration: underline;
          }
          .quill-content blockquote {
            border-left: 4px solid #e5e7eb;
            padding-left: 1em;
            margin-left: 0;
            margin-bottom: 1em;
            font-style: italic;
            color: #6b7280;
          }
        `}
      </style>
      <div className="bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-xl shadow-2xl">
        <CardHeader className="flex flex-row items-center justify-between border-b p-6">
          <div>
            <CardTitle className="text-2xl font-bold">Clinical Note</CardTitle>
            <p className="text-slate-500">
              {note.date
                ? format(new Date(note.date), "MMMM d, yyyy")
                : "No date"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={() => onEdit(note)}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handlePrint}>
              <Printer className="w-4 h-4" />
            </Button>
            <Button
              variant="destructive"
              size="icon"
              onClick={() => onDelete(note.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-8 print:p-4">
          {/* Patient and Doctor Info */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-500" />
              <div>
                <div className="text-sm text-slate-500">Patient</div>
                <div className="font-semibold text-slate-900">
                  {patient?.name || "Unknown Patient"}
                  {patient?.email && (
                    <div className="text-sm text-slate-600">
                      {patient.email}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-500" />
              <div>
                <div className="text-sm text-slate-500">Provider</div>
                <div className="font-semibold text-slate-900">
                  {doctor?.name || "Unknown Provider"}
                </div>
              </div>
            </div>
          </div>

          {/* Consultation Notes */}
          {note.consultation_notes && (
            <Section title="Consultation Notes" icon={FileText}>
              {renderHtmlContent(note.consultation_notes)}
            </Section>
          )}

          {/* Diagnosis */}
          {note.diagnosis && (
            <Section title="Diagnosis" icon={Stethoscope}>
              {renderHtmlContent(note.diagnosis)}
            </Section>
          )}

          {/* Treatment Plan */}
          {note.treatment_plan && (
            <Section title="Treatment Plan" icon={Clipboard}>
              {renderHtmlContent(note.treatment_plan)}
            </Section>
          )}

          {/* Metadata */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex justify-between text-sm text-slate-500">
              <div>
                Created:{" "}
                {note.createdAt
                  ? format(new Date(note.createdAt), "MMM d, yyyy h:mm a")
                  : "Unknown"}
              </div>
              {note.updatedAt && (
                <div>
                  Last Updated:{" "}
                  {format(new Date(note.updatedAt), "MMM d, yyyy h:mm a")}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </div>
    </>
  );
}
